<!-- src/lib/components/RunningPanel.svelte -->
<script lang="ts">
  import { onDestroy } from 'svelte';
  import { slide } from 'svelte/transition';
  import { agentStore } from '../stores/agents.svelte.ts';
  import { api } from '../api.ts';
  import { toast } from '../stores/toast.svelte.ts';

  interface Props {
    onSelectAgent: (agent_id: string) => void;
  }
  let { onSelectAgent }: Props = $props();

  let now = $state(Date.now());
  const tick = setInterval(() => {
    now = Date.now();
  }, 1000);
  onDestroy(() => clearInterval(tick));

  let expanded = $state<Record<string, boolean>>({});

  function toggleExpand(agent_id: string): void {
    expanded = { ...expanded, [agent_id]: !expanded[agent_id] };
  }

  function stripAnsi(s: string): string {
    return s.replace(/\x1B\[[0-9;?]*[ -/]*[@-~]/g, '');
  }

  function fullOutput(agent_id: string): string {
    return stripAnsi((agentStore.outputs[agent_id] ?? []).join(''));
  }

  function previewOutput(agent_id: string): string {
    const chunks = agentStore.outputs[agent_id] ?? [];
    return stripAnsi(chunks.slice(-5).join('').slice(-300)).trim();
  }

  function lineCount(agent_id: string): number {
    return stripAnsi((agentStore.outputs[agent_id] ?? []).join(''))
      .split('\n')
      .filter(Boolean)
      .length;
  }

  function elapsed(startedAt: string): string {
    const ms = now - new Date(startedAt).getTime();
    if (ms < 0) return '0s';
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return `${m}m ${rem}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }

  async function cancelAgent(agent_id: string, prompt: string): Promise<void> {
    const label = prompt.length > 48 ? prompt.slice(0, 48) + '…' : prompt || agent_id.slice(0, 8);
    if (!window.confirm(`Stop agent: ${label}?`)) return;
    try {
      await api(`/api/v1/tasks/${agent_id}`, { method: 'DELETE' });
      toast.push({ title: 'Agent cancelled', body: label, kind: 'warning' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.push({ title: 'Cancel failed', body: msg, kind: 'error' });
    }
  }

  function scrollToBottom(node: HTMLElement, _trigger: number) {
    node.scrollTop = node.scrollHeight;
    return {
      update(_next: number) {
        node.scrollTop = node.scrollHeight;
      },
    };
  }
</script>

<section class="panel">
  <header>
    <h2>Running</h2>
    <span class="badge">{agentStore.running.length}</span>
    {#if agentStore.running.length > 0}
      <span class="capacity">/ {agentStore.maxParallel}</span>
    {/if}
  </header>

  {#if agentStore.running.length === 0}
    <p class="empty">No agents running.</p>
  {:else}
    <ul>
      {#each agentStore.running as agent (agent.agent_id)}
        {@const lines = lineCount(agent.agent_id)}
        {@const isExpanded = expanded[agent.agent_id] ?? false}
        <li class="agent-card" transition:slide={{ duration: 200 }}>
          <div class="agent-header">
            <span class="dot running" aria-hidden="true"></span>
            <span class="prompt" title={agent.prompt}>
              {agent.prompt.length > 52 ? agent.prompt.slice(0, 52) + '…' : agent.prompt || agent.agent_id.slice(0, 8)}
            </span>
            <span class="elapsed">{elapsed(agent.started_at)}</span>
          </div>

          <div class="agent-meta">
            {#if agent.tier}
              <span class="tier-chip">{agent.tier}</span>
            {/if}
            {#if lines > 0}
              <span class="lines-chip">{lines} lines</span>
            {/if}
            <div class="meta-actions">
              <button class="action-btn" onclick={() => toggleExpand(agent.agent_id)}>
                {isExpanded ? 'Collapse' : 'Expand'}
              </button>
              <button class="action-btn snoop-btn" onclick={() => onSelectAgent(agent.agent_id)}>Snoop →</button>
              <button class="action-btn cancel-btn" onclick={() => void cancelAgent(agent.agent_id, agent.prompt)}>Cancel</button>
            </div>
          </div>

          {#if isExpanded}
            <pre
              class="output-full"
              use:scrollToBottom={agentStore.outputs[agent.agent_id]?.length ?? 0}
            >{fullOutput(agent.agent_id) || '(no output yet)'}</pre>
          {:else if previewOutput(agent.agent_id)}
            <pre class="preview">{previewOutput(agent.agent_id)}</pre>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .panel {
    background: oklch(0.74 0.28 310 / 0.06);
    border: 1px solid oklch(0.74 0.28 310 / 0.16);
    border-radius: 10px;
    padding: 1rem 1.25rem;
  }
  header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-bottom: 0.875rem;
  }
  h2 {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: oklch(0.80 0.26 310);
    margin: 0;
  }
  .badge {
    background: oklch(0.74 0.28 310 / 0.16);
    color: oklch(0.80 0.26 310);
    border-radius: 12px;
    padding: 0.1rem 0.5rem;
    font-size: 0.7rem;
    font-family: 'JetBrains Mono', monospace;
  }
  .capacity {
    font-size: 0.7rem;
    color: oklch(0.45 0.04 310 / 0.45);
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .agent-card {
    background: oklch(0.10 0.018 275 / 0.60);
    border: 1px solid oklch(0.74 0.28 310 / 0.14);
    border-radius: 8px;
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    animation: card-glow 3s ease-in-out infinite;
  }
  @keyframes card-glow {
    0%, 100% { box-shadow: 0 0 8px oklch(0.74 0.28 310 / 0.09); }
    50% { box-shadow: 0 0 16px oklch(0.74 0.28 310 / 0.16); }
  }
  .agent-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .dot.running {
    background: oklch(0.72 0.19 145);
    box-shadow: 0 0 8px oklch(0.72 0.19 145 / 0.70);
    animation: run-pulse 1.5s ease-in-out infinite;
  }
  @keyframes run-pulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 8px oklch(0.72 0.19 145 / 0.70); }
    50% { opacity: 0.55; box-shadow: 0 0 3px oklch(0.72 0.19 145 / 0.30); }
  }
  .prompt {
    flex: 1;
    font-size: 0.82rem;
    color: oklch(0.88 0.06 310);
    min-width: 0;
  }
  .elapsed {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.68rem;
    color: oklch(0.65 0.08 310 / 0.80);
    white-space: nowrap;
    flex-shrink: 0;
  }
  .agent-meta {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
  }
  .tier-chip,
  .lines-chip {
    font-size: 0.60rem;
    font-weight: 600;
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
    font-family: 'JetBrains Mono', monospace;
  }
  .tier-chip {
    background: oklch(0.55 0.20 265 / 0.14);
    color: oklch(0.65 0.18 265);
    border: 1px solid oklch(0.55 0.20 265 / 0.22);
  }
  .lines-chip {
    background: oklch(0.72 0.18 145 / 0.10);
    color: oklch(0.72 0.18 145 / 0.80);
    border: 1px solid oklch(0.72 0.18 145 / 0.20);
  }
  .meta-actions {
    margin-left: auto;
    display: flex;
    gap: 0.3rem;
  }
  .action-btn {
    background: oklch(0.14 0.02 275 / 0.70);
    border: 1px solid oklch(0.30 0.05 275 / 0.30);
    color: oklch(0.65 0.06 310 / 0.80);
    padding: 0.18rem 0.55rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.68rem;
    font-family: inherit;
    transition: background 100ms ease, color 100ms ease, border-color 100ms ease;
    white-space: nowrap;
  }
  .action-btn:hover {
    background: oklch(0.18 0.03 275);
    color: oklch(0.88 0.08 310);
  }
  .snoop-btn:hover {
    border-color: oklch(0.55 0.20 265 / 0.40);
    color: oklch(0.65 0.20 265);
  }
  .cancel-btn:hover {
    border-color: oklch(0.62 0.22 25 / 0.40);
    color: oklch(0.72 0.20 25);
  }
  pre.preview {
    background: oklch(0.07 0.012 275 / 0.80);
    color: oklch(0.74 0.28 310 / 0.64);
    font-size: 0.68rem;
    line-height: 1.4;
    padding: 0.5rem 0.625rem;
    border-radius: 4px;
    overflow: hidden;
    max-height: 3.5rem;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;
    font-family: 'JetBrains Mono', monospace;
  }
  pre.output-full {
    background: oklch(0.07 0.012 275 / 0.80);
    color: oklch(0.78 0.06 310 / 0.85);
    font-size: 0.68rem;
    line-height: 1.45;
    padding: 0.6rem 0.75rem;
    border-radius: 4px;
    overflow-y: auto;
    max-height: 320px;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;
    font-family: 'JetBrains Mono', monospace;
  }
  .empty {
    color: oklch(0.45 0.04 310 / 0.55);
    font-size: 0.82rem;
    font-style: italic;
    margin: 0.5rem 0;
  }
  @media (prefers-reduced-motion: reduce) {
    .dot.running { animation: none; }
    .agent-card { animation: none; }
  }
</style>
